import {
  stateError,
  stateInitial,
  stateLoaded,
  stateLoading,
} from "@shared/models/state";
import {
  type Applink,
  getRegisteredAppLinks,
} from "@shared/services/appLinkRegistry";
import { type TwinResponse } from "@shared/services/twinManagement";
import { Portal, type PortalTypes } from "@inctec/smx-ui-client";
import { defineStore } from "pinia";
import { computed, ref } from "vue";

export type { Applink } from "@shared/services/appLinkRegistry";

type AppItem = PortalTypes.AppRecord;

export const useAppStore = defineStore("apps", () => {
  const appsLoadingState = ref({
    ...stateInitial,
    query: {},
    result: [] as AppItem[],
  });

  const applinks = ref<Applink[]>(getRegisteredAppLinks());
  let appsOverviewAbortController: AbortController | null = null;

  function setApplinks(links: Applink[]) {
    applinks.value = applinks.value.concat(links);
  }

  // Get a router link for a uri, this is currently used to retrieve a link for twin editing, using the
  // Twin edit URI: twin-edit.${twin.type}.${twin.templateUuid}.${twin.uuid}
  function getLink(uri: string) {
    const link = applinks.value.find((item) =>
      new RegExp(item.uriExpression).test(uri),
    );
    // If no link is found, navigate to home
    if (link === undefined) {
      return { uriExpression: "error", routeName: "home" };
    }
    return link;
  }

  function getTwinEditLink(twin: TwinResponse) {
    return getLink(`twin-edit.${twin.type}.${twin.templateUuid}.${twin.uuid}`);
  }

  async function loadAppsOverview(
    query: Record<string, unknown>,
    reload?: boolean,
  ) {
    if (appsLoadingState.value.loading) {
      if (reload) {
        appsOverviewAbortController?.abort();
      } else {
        return;
      }
    }
    if (!reload && appsLoadingState.value.loaded) {
      return;
    }

    const abortController = new AbortController();
    appsOverviewAbortController = abortController;
    appsLoadingState.value = {
      ...stateLoading,
      query,
      result: appsLoadingState.value.result,
    };
    try {
      const response = await Portal.getApps({
        signal: abortController.signal,
      });
      if (abortController.signal.aborted) {
        return;
      }
      appsLoadingState.value = {
        ...stateLoaded,
        result: response.data.result ?? [],
        query,
      };
    } catch (e) {
      if (abortController.signal.aborted) {
        return;
      }
      appsLoadingState.value = { ...appsLoadingState.value, ...stateError };
      throw e;
    } finally {
      if (appsOverviewAbortController === abortController) {
        appsOverviewAbortController = null;
      }
    }
  }

  const appsSettingsState = ref({ ...stateInitial, result: [] as AppItem[] });

  async function loadAppsSettings() {
    appsSettingsState.value = {
      ...stateLoading,
      result: appsSettingsState.value.result,
    };
    const response = await Portal.getApps({
      query: { admin: "true" },
    }).catch((e) => {
      appsSettingsState.value = { ...appsSettingsState.value, ...stateError };
      throw e;
    });
    appsSettingsState.value = {
      ...stateLoaded,
      result: response.data.result ?? [],
    };
  }

  const appsSettingsLoading = ref(false);

  async function saveAppSettings(appId: string, settings: PortalTypes.App) {
    appsSettingsLoading.value = true;
    try {
      const response = await Portal.patchApp({
        path: { id: appId },
        body: settings,
      });
      await loadAppsSettings();
      return response;
    } finally {
      appsSettingsLoading.value = false;
    }
  }

  async function createAppSettings(settings: PortalTypes.App) {
    appsSettingsLoading.value = true;
    try {
      const response = await Portal.createAppConfig({ body: settings });
      await loadAppsSettings();
      return response;
    } finally {
      appsSettingsLoading.value = false;
    }
  }

  async function addAppRole(app: string, role: string) {
    await Portal.addRole({
      path: { id: app },
      query: { id: app },
      body: { name: role },
    });
  }

  async function deleteAppRole(app: string, roleName: string) {
    await Portal.deleteRole({ path: { id: app, roleName } });
  }

  const sortedResult = computed(() => {
    return appsLoadingState.value.result
      .filter((item) => (item.order ?? -1) >= 0)
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  });

  return {
    loadAppsOverview,
    appsLoadingState,
    loadAppsSettings,
    appsSettingsState,
    saveAppSettings,
    createAppSettings,
    appsSettingsLoading,
    addAppRole,
    deleteAppRole,
    getLink,
    getTwinEditLink,
    setApplinks,
    sortedResult,
  };
});
